import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpenText, LoaderCircle } from 'lucide-react'
import {
  createAdminStudioRecipe,
  listAdminStudioRecipes,
  pilotAdminStudioRecipe,
  replaceIncompatibleAdminStudioRecipe,
} from '../../api/generated/admin-studio-generation/admin-studio-generation'
import type {
  StudioRecipeCreate,
  StudioRecipeRead,
  StudioRecipeReplacementRequest,
} from '../../api/generated/model'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { USE_REAL_API } from '../../lib/api'
import { parseApiProblem } from '../../lib/api-problem'
import { toast } from '../../lib/toast'
import { useT, pickT } from '../../i18n'
import { useLocaleStore } from '../../store/locale'
import { catalogStudioRecipeI18n } from '../../i18n/admin/catalog-studio-recipe'
import { DEFAULT_JEWELRY_CATALOG_PROMPT } from './catalog-studio-prompt'

export { DEFAULT_JEWELRY_CATALOG_PROMPT } from './catalog-studio-prompt'

const RECIPE_QUERY_KEY = ['admin', 'studio-generation', 'recipes'] as const
const RECIPE_OPERATION = 'seedream_v5_pro_edit' as const
const DEFAULT_RECIPE_NAME = 'Jewelry catalog — scene parity'
const PENDING_CREATE_KEY = 'pexify:catalog-studio-recipe:pending-create'
const PENDING_REPAIR_KEY = 'pexify:catalog-studio-recipe:pending-repair'

type CreateIntent = { body: StudioRecipeCreate; idempotencyKey: string }
type RepairIntent = {
  recipeId: string
  body: StudioRecipeReplacementRequest
  idempotencyKey: string
}

function loadPendingCreateIntent(): CreateIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_CREATE_KEY)
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object') return null
    const intent = value as Partial<CreateIntent>
    if (
      typeof intent.idempotencyKey !== 'string'
      || !intent.idempotencyKey.startsWith('media-generation-recipe-create:')
      || intent.body?.name !== DEFAULT_RECIPE_NAME
      || intent.body.operation !== RECIPE_OPERATION
      || typeof intent.body.prompt !== 'string'
      || !intent.body.prompt.trim()
      || intent.body.parameters?.resolution !== '1.5k'
      || intent.body.parameters.aspect_ratio !== 'square_1_1'
    ) return null
    return intent as CreateIntent
  } catch {
    return null
  }
}

function persistPendingCreateIntent(intent: CreateIntent): void {
  try {
    sessionStorage.setItem(PENDING_CREATE_KEY, JSON.stringify(intent))
  } catch {
    const t = pickT(catalogStudioRecipeI18n, useLocaleStore.getState().locale)
    throw Object.assign(new Error(t.errors.sessionStorageRequired), { status: 422 })
  }
}

function clearPendingCreateIntent(): void {
  try { sessionStorage.removeItem(PENDING_CREATE_KEY) } catch { /* storage unavailable */ }
}

function loadPendingRepairIntent(): RepairIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_REPAIR_KEY)
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object') return null
    const intent = value as Partial<RepairIntent>
    if (
      typeof intent.recipeId !== 'string'
      || typeof intent.idempotencyKey !== 'string'
      || !intent.idempotencyKey.startsWith('media-generation-recipe-repair:')
      || typeof intent.body?.prompt !== 'string'
      || !intent.body.prompt.trim()
      || intent.body.parameters?.resolution !== '1.5k'
      || intent.body.parameters.aspect_ratio !== 'square_1_1'
    ) return null
    return intent as RepairIntent
  } catch {
    return null
  }
}

function persistPendingRepairIntent(intent: RepairIntent): void {
  try {
    sessionStorage.setItem(PENDING_REPAIR_KEY, JSON.stringify(intent))
  } catch {
    const t = pickT(catalogStudioRecipeI18n, useLocaleStore.getState().locale)
    throw Object.assign(new Error(t.errors.sessionStorageRequired), { status: 422 })
  }
}

function clearPendingRepairIntent(): void {
  try { sessionStorage.removeItem(PENDING_REPAIR_KEY) } catch { /* storage unavailable */ }
}

function isCatalogRecipe(recipe: StudioRecipeRead): boolean {
  return recipe.operation === RECIPE_OPERATION
    && recipe.name === DEFAULT_RECIPE_NAME
    && recipe.parameters.resolution === '1.5k'
    && recipe.parameters.aspect_ratio === 'square_1_1'
}

async function readRecipes(): Promise<StudioRecipeRead[]> {
  const response = await listAdminStudioRecipes()
  if (response.status !== 200) {
    throw new Error(pickT(catalogStudioRecipeI18n, useLocaleStore.getState().locale).errors.recipesLoadFailed)
  }
  return response.data.items
}

export function CatalogStudioRecipeCard() {
  const t = useT(catalogStudioRecipeI18n)
  const queryClient = useQueryClient()
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const [initialIntent] = useState(loadPendingCreateIntent)
  const [initialRepairIntent] = useState(loadPendingRepairIntent)
  const [prompt, setPrompt] = useState(initialIntent?.body.prompt ?? DEFAULT_JEWELRY_CATALOG_PROMPT)
  const [pilotTarget, setPilotTarget] = useState<StudioRecipeRead | null>(null)
  const [repairTarget, setRepairTarget] = useState<StudioRecipeRead | null>(null)
  const [repairIntentDismissed, setRepairIntentDismissed] = useState(false)
  const [replacementPrompt, setReplacementPrompt] = useState(
    initialRepairIntent?.body.prompt ?? DEFAULT_JEWELRY_CATALOG_PROMPT,
  )
  const [createOutcomeUnknown, setCreateOutcomeUnknown] = useState(Boolean(initialIntent))
  const [repairOutcomeUnknown, setRepairOutcomeUnknown] = useState(Boolean(initialRepairIntent))
  const createIntent = useRef<CreateIntent | null>(initialIntent)
  const repairIntent = useRef<RepairIntent | null>(initialRepairIntent)
  const recipes = useQuery({
    queryKey: RECIPE_QUERY_KEY,
    queryFn: readRecipes,
    enabled: USE_REAL_API,
  })
  const catalogRecipes = recipes.data?.filter(isCatalogRecipe) ?? []
  const restoredRepairTarget = !repairIntentDismissed && initialRepairIntent
    ? recipes.data?.find((recipe) => recipe.id === initialRepairIntent.recipeId) ?? null
    : null
  const visibleRepairTarget = repairTarget ?? restoredRepairTarget
  const hasSelectableRecipe = recipes.data?.some(
    (recipe) => recipe.operation === RECIPE_OPERATION && (recipe.status === 'pilot' || recipe.status === 'active'),
  ) ?? false
  const create = useMutation({
    mutationFn: () => {
      createIntent.current ??= {
        body: {
          name: DEFAULT_RECIPE_NAME,
          operation: RECIPE_OPERATION,
          prompt: prompt.trim(),
          parameters: { resolution: '1.5k', aspect_ratio: 'square_1_1' },
        },
        idempotencyKey: `media-generation-recipe-create:${crypto.randomUUID()}`,
      }
      persistPendingCreateIntent(createIntent.current)
      return createAdminStudioRecipe(createIntent.current.body, {
        headers: { 'Idempotency-Key': createIntent.current.idempotencyKey },
      })
    },
    onSuccess: async () => {
      if (!mountedRef.current) return
      createIntent.current = null
      clearPendingCreateIntent()
      setCreateOutcomeUnknown(false)
      toast.success(t.toasts.createSuccessTitle, {
        description: t.toasts.createSuccessDescription,
        audience: 'admin',
      })
      await queryClient.invalidateQueries({ queryKey: RECIPE_QUERY_KEY })
    },
    onError: (error: unknown) => {
      if (!mountedRef.current) return
      const status = parseApiProblem(error, t.errors.genericRequestFailed).status
      if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429) {
        createIntent.current = null
        clearPendingCreateIntent()
        setCreateOutcomeUnknown(false)
      } else {
        setCreateOutcomeUnknown(true)
      }
      toast.error(t.toasts.createErrorTitle, {
        description: parseApiProblem(error, t.errors.genericRequestFailed).message,
        audience: 'admin',
      })
    },
  })
  const pilot = useMutation({
    mutationFn: async (recipeId: string) => {
      const current = await readRecipes()
      if (!mountedRef.current) throw new Error(t.errors.pilotSubmittedAfterUnmount)
      if (current.some(
        (recipe) => recipe.operation === RECIPE_OPERATION && (recipe.status === 'pilot' || recipe.status === 'active'),
      )) {
        throw new Error(t.errors.pilotAlreadyManaged)
      }
      return pilotAdminStudioRecipe(recipeId)
    },
    onSuccess: async () => {
      if (!mountedRef.current) return
      setPilotTarget(null)
      toast.success(t.toasts.pilotSuccessTitle, {
        description: t.toasts.pilotSuccessDescription,
        audience: 'admin',
      })
      await queryClient.invalidateQueries({ queryKey: RECIPE_QUERY_KEY })
    },
    onError: (error: unknown) => {
      if (!mountedRef.current) return
      void queryClient.invalidateQueries({ queryKey: RECIPE_QUERY_KEY })
      toast.error(t.toasts.pilotErrorTitle, {
        description: parseApiProblem(error, t.errors.genericRequestFailed).message,
        audience: 'admin',
      })
    },
  })
  const repair = useMutation({
    mutationFn: (recipeId: string) => {
      repairIntent.current ??= {
        recipeId,
        body: {
          prompt: replacementPrompt.trim(),
          parameters: { resolution: '1.5k', aspect_ratio: 'square_1_1' },
        },
        idempotencyKey: `media-generation-recipe-repair:${crypto.randomUUID()}`,
      }
      persistPendingRepairIntent(repairIntent.current)
      return replaceIncompatibleAdminStudioRecipe(
        repairIntent.current.recipeId,
        repairIntent.current.body,
        { headers: { 'Idempotency-Key': repairIntent.current.idempotencyKey } },
      )
    },
    onSuccess: async () => {
      if (!mountedRef.current) return
      repairIntent.current = null
      clearPendingRepairIntent()
      setRepairOutcomeUnknown(false)
      setRepairIntentDismissed(true)
      setRepairTarget(null)
      toast.success(t.toasts.repairSuccessTitle, {
        description: t.toasts.repairSuccessDescription,
        audience: 'admin',
      })
      await queryClient.invalidateQueries({ queryKey: RECIPE_QUERY_KEY })
    },
    onError: (error: unknown) => {
      if (!mountedRef.current) return
      const problem = parseApiProblem(error, t.errors.genericRequestFailed)
      const status = problem.status
      if (status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 409 && status !== 429) {
        repairIntent.current = null
        clearPendingRepairIntent()
        setRepairOutcomeUnknown(false)
        setRepairIntentDismissed(true)
      } else {
        setRepairOutcomeUnknown(true)
      }
      toast.error(t.toasts.repairErrorTitle, {
        description: problem.message,
        audience: 'admin',
      })
    },
  })

  const openRepair = (recipe: StudioRecipeRead) => {
    if (!repairOutcomeUnknown) {
      repairIntent.current = null
      setReplacementPrompt(DEFAULT_JEWELRY_CATALOG_PROMPT)
    }
    setRepairIntentDismissed(false)
    setRepairTarget(recipe)
  }

  return (
    <section
      className="overflow-hidden rounded-lg bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]"
      aria-labelledby="catalog-studio-recipe-title"
    >
      <div className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] text-[var(--color-info)]">
            <BookOpenText size={18} aria-hidden="true" />
          </div>
          <div>
            <h2 id="catalog-studio-recipe-title" className="text-sm font-semibold text-[var(--color-text)]">
              {t.header.title}
            </h2>
            <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-muted)]">
              {t.header.subtitle}
            </p>
          </div>
        </div>
        <span className="shrink-0 rounded bg-[color-mix(in_srgb,var(--color-info)_12%,var(--color-surface))] px-2 py-1 text-[10px] font-semibold text-[var(--color-info)] ring-1 ring-[color-mix(in_srgb,var(--color-info)_38%,var(--color-border))]">
          {t.header.draftToPilotBadge}
        </span>
      </div>

      <div className="space-y-4 px-5 py-5">
        <div className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-xs leading-5 text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
          {t.infoBanner}
        </div>

        {!USE_REAL_API ? (
          <>
            <div className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
              {t.previewModeBanner}
            </div>
            <DraftForm
              t={t}
              name={DEFAULT_RECIPE_NAME}
              prompt={prompt}
              pending={false}
              disabled
              onPromptChange={setPrompt}
              onCreate={() => undefined}
            />
          </>
        ) : recipes.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-xs text-[var(--color-text-muted)]" role="status">
            <LoaderCircle size={14} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {t.loadingRecipes}
          </div>
        ) : recipes.error ? (
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-danger)] ring-1 ring-[color-mix(in_srgb,var(--color-danger)_42%,var(--color-border))]">
            {t.loadError}
          </div>
        ) : catalogRecipes.length ? (
          <RecipeList
            t={t}
            recipes={catalogRecipes}
            onPilot={setPilotTarget}
            onRepair={openRepair}
            pilotPending={pilot.isPending}
            repairPending={repair.isPending}
            hasSelectableRecipe={hasSelectableRecipe}
          />
        ) : (
          <>
            <DraftForm
              t={t}
              name={DEFAULT_RECIPE_NAME}
              prompt={prompt}
              pending={create.isPending}
              disabled={createOutcomeUnknown}
              onPromptChange={setPrompt}
              onCreate={() => create.mutate()}
            />
            {createOutcomeUnknown && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]" role="alert">
                <span>{t.unknownOutcomeBanner}</span>
                <Button type="button" size="sm" variant="outline" disabled={create.isPending} onClick={() => create.mutate()}>
                  {t.retrySameRequest}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog
        open={Boolean(pilotTarget)}
        onOpenChange={(open) => !open && !pilot.isPending && setPilotTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.pilotDialog.title}</DialogTitle>
            <DialogDescription>
              {pilotTarget && t.pilotDialog.description(pilotTarget.name, pilotTarget.version)}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] px-3 py-2 text-sm text-[var(--color-success)] ring-1 ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]">
            {t.pilotDialog.note}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={pilot.isPending} onClick={() => setPilotTarget(null)}>
              {t.pilotDialog.cancel}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!pilotTarget || pilot.isPending}
              onClick={() => pilotTarget && pilot.mutate(pilotTarget.id)}
            >
              {pilot.isPending && <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t.pilotDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(visibleRepairTarget)}
        onOpenChange={(open) => {
          if (!open && !repair.isPending) {
            setRepairIntentDismissed(true)
            setRepairTarget(null)
          }
        }}
      >
        <DialogContent className="max-w-2xl" aria-busy={repair.isPending}>
          <DialogHeader>
            <DialogTitle>{t.repairDialog.title}</DialogTitle>
            <DialogDescription>
              {visibleRepairTarget && t.repairDialog.description(visibleRepairTarget.name, visibleRepairTarget.version)}
            </DialogDescription>
          </DialogHeader>
          {visibleRepairTarget?.compatibility.actual_chars != null
            && visibleRepairTarget.compatibility.max_chars != null && (
            <div className="rounded-md bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] px-3 py-2 text-sm text-[var(--color-danger)] ring-1 ring-[color-mix(in_srgb,var(--color-danger)_42%,var(--color-border))]" role="alert">
              {t.recipeList.compatibilityEvidence(
                visibleRepairTarget.compatibility.actual_chars,
                visibleRepairTarget.compatibility.max_chars,
              )}
            </div>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">
              {t.repairDialog.promptLabel}
            </span>
            <textarea
              value={replacementPrompt}
              onChange={(event) => setReplacementPrompt(event.target.value)}
              disabled={repair.isPending || repairOutcomeUnknown}
              rows={12}
              maxLength={20_000}
              className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs leading-5 text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]"
            />
          </label>
          <div className="rounded-md bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] px-3 py-2 text-sm text-[var(--color-success)] ring-1 ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]">
            {t.repairDialog.noSpendNote}
          </div>
          {repairOutcomeUnknown && (
            <div className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-sm text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]" role="alert">
              {t.repairDialog.unknownOutcome}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={repair.isPending}
              onClick={() => {
                setRepairIntentDismissed(true)
                setRepairTarget(null)
              }}
            >
              {t.repairDialog.cancel}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!visibleRepairTarget || repair.isPending || !replacementPrompt.trim()}
              onClick={() => visibleRepairTarget && repair.mutate(visibleRepairTarget.id)}
            >
              {repair.isPending && <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {repairOutcomeUnknown ? t.repairDialog.retry : t.repairDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

type CatalogStudioRecipeStrings = (typeof catalogStudioRecipeI18n)['en']

function RecipeList({
  t,
  recipes,
  onPilot,
  onRepair,
  pilotPending,
  repairPending,
  hasSelectableRecipe,
}: {
  t: CatalogStudioRecipeStrings
  recipes: StudioRecipeRead[]
  onPilot: (recipe: StudioRecipeRead) => void
  onRepair: (recipe: StudioRecipeRead) => void
  pilotPending: boolean
  repairPending: boolean
  hasSelectableRecipe: boolean
}) {
  return (
    <div className="space-y-3">
      {hasSelectableRecipe && recipes.some((recipe) => recipe.status === 'draft') && (
        <p className="rounded-md bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-warning)] ring-1 ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]">
          {t.recipeList.blockedNote}
        </p>
      )}
      {recipes.map((recipe) => {
        const hasCompatibleActiveSuccessor = recipes.some(
          (candidate) => candidate.family_id === recipe.family_id
            && candidate.status === 'active'
            && candidate.compatibility.compatible,
        )
        return (
        <article key={recipe.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-[var(--color-text)]">{recipe.name}</h3>
                <span className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
                  v{recipe.version}
                </span>
                <StatusBadge t={t} status={recipe.status} />
                <CompatibilityBadge t={t} compatible={recipe.compatibility.compatible} />
              </div>
              <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                {t.recipeList.specSummary(
                  String(recipe.parameters.resolution ?? '—'),
                  String(recipe.parameters.aspect_ratio ?? '—'),
                )}
              </p>
            </div>
            {recipe.status === 'pilot'
              && !recipe.compatibility.compatible
              && !hasCompatibleActiveSuccessor ? (
              <Button type="button" size="sm" variant="primary" disabled={repairPending} onClick={() => onRepair(recipe)}>
                {t.recipeList.createCompatibleReplacement}
              </Button>
            ) : recipe.status === 'pilot'
              && !recipe.compatibility.compatible
              && hasCompatibleActiveSuccessor ? (
              <span className="text-xs font-medium text-[var(--color-success)]">
                {t.recipeList.replacedHistory}
              </span>
            ) : recipe.status === 'draft' && (
              <Button type="button" size="sm" variant="primary" disabled={pilotPending || hasSelectableRecipe} onClick={() => onPilot(recipe)}>
                {t.recipeList.makePilot}
              </Button>
            )}
          </div>
          {!recipe.compatibility.compatible
            && recipe.compatibility.actual_chars != null
            && recipe.compatibility.max_chars != null && (
            <div className="mt-3 rounded-md bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] px-3 py-2 text-xs text-[var(--color-danger)] ring-1 ring-[color-mix(in_srgb,var(--color-danger)_42%,var(--color-border))]" role="alert">
              {t.recipeList.compatibilityEvidence(
                recipe.compatibility.actual_chars,
                recipe.compatibility.max_chars,
              )}
            </div>
          )}
          <details className="mt-3 border-t border-[var(--color-border)] pt-3">
            <summary className="cursor-pointer text-xs font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              {t.recipeList.showPrompt}
            </summary>
            <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-[var(--color-surface)] p-3 font-mono text-[11px] leading-5 text-[var(--color-text)] ring-1 ring-[var(--color-border)]">
              {recipe.prompt}
            </pre>
          </details>
        </article>
        )
      })}
    </div>
  )
}

function CompatibilityBadge({
  t,
  compatible,
}: {
  t: CatalogStudioRecipeStrings
  compatible: boolean
}) {
  const tone = compatible
    ? 'bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] text-[var(--color-success)] ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]'
    : 'bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] text-[var(--color-danger)] ring-[color-mix(in_srgb,var(--color-danger)_42%,var(--color-border))]'
  const label = compatible ? t.recipeList.compatible : t.recipeList.incompatible
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${tone}`}>{label}</span>
}

function StatusBadge({ t, status }: { t: CatalogStudioRecipeStrings; status: StudioRecipeRead['status'] }) {
  const tone =
    status === 'pilot' || status === 'active'
      ? 'bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] text-[var(--color-success)] ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]'
      : status === 'retired'
        ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] ring-[var(--color-border)]'
        : 'bg-[color-mix(in_srgb,var(--color-warning)_12%,var(--color-surface))] text-[var(--color-warning)] ring-[color-mix(in_srgb,var(--color-warning)_38%,var(--color-border))]'
  const label = t.statusLabels[status]
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${tone}`}>{label}</span>
}

function DraftForm({
  t,
  name,
  prompt,
  pending,
  disabled = false,
  onPromptChange,
  onCreate,
}: {
  t: CatalogStudioRecipeStrings
  name: string
  prompt: string
  pending: boolean
  disabled?: boolean
  onPromptChange: (value: string) => void
  onCreate: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="sm:col-span-2">
          <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.draftForm.recipeNameLabel}</span>
          <input
            value={name}
            readOnly
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)] outline-none"
          />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.draftForm.methodLabel}</span>
          <input
            value="Seedream 5 Pro Edit"
            readOnly
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)] outline-none"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.draftForm.resolutionLabel}</span>
            <input
              value="1.5K"
              readOnly
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)] outline-none"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.draftForm.aspectRatioLabel}</span>
            <input
              value={t.draftForm.aspectRatioValue}
              readOnly
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-sm text-[var(--color-text)] outline-none"
            />
          </label>
        </div>
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-[var(--color-text)]">{t.draftForm.promptLabel}</span>
        <textarea
          value={prompt}
          onChange={(event) => onPromptChange(event.target.value)}
          disabled={disabled}
          rows={12}
          maxLength={20000}
          className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs leading-5 text-[var(--color-text)] outline-none focus:border-indigo-300 focus:shadow-[0_0_0_3px_rgb(238_242_255)]"
        />
      </label>

      <div className="flex justify-end border-t border-[var(--color-border)] pt-4">
        <Button
          type="button"
          variant="primary"
          disabled={disabled || pending || !name.trim() || !prompt.trim()}
          onClick={onCreate}
        >
          {pending && <LoaderCircle size={14} className="mr-1.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {pending ? t.draftForm.creating : t.draftForm.createDraft}
        </Button>
      </div>
    </div>
  )
}
